import {Component} from '@wonderlandengine/api';
import {InputManager, KeyEventType} from '../classes/InputManager.js';
import {TimeManager} from './time-manager.js';
import {GameFlowController} from './game-flow-controller.js';

enum TouchState {
    // No State = No touch
    None,
    // Touch is stationary
    Stationary,
    // Touch has just started
    Began,
    // Touch is moving
    Moved,
    // Touch has ended
    Ended,
    // Touch has been cancelled
    Cancelled,
}

/**
 * This component is helps to get touch (and mouse) input.
 * It's a different implementation that the unity one, but uses a similar InputManager,
 * so impact on other code should be minimal.
 */
export class TouchController extends Component {
    static TypeName = 'touch-controller';

    private _pointerPressed: boolean;
    private _pointerDown: boolean;
    private _pointerUp: boolean;
    private _inputManager: InputManager;
    private _pointerPos: number[] = [-1, -1];
    private _previousPointerPos: number[] = [-1, -1];
    private _wasPointerDown = false;
    private _wasPointerUp = false;

    private _state: TouchState = TouchState.Stationary;
    private _pointerMoved: boolean;
    private _wasPointerMoved: boolean;

    start(): void {
        this._inputManager = InputManager.instance;
        this._pointerPressed = false;
        this._pointerDown = false;
        this._pointerUp = false;
    }

    update(delta: number): void {
        if (TimeManager.instance.paused && !GameFlowController.instance.tutorialRunning) {
            // game is paused
            return;
        }

        this._state = TouchState.None;

        if (this._pointerDown) {
            if (!this._wasPointerDown) {
                this._wasPointerDown = true;
                this._state = TouchState.Began;
            } else {
                if (this._pointerMoved && !this._wasPointerMoved) {
                    this._wasPointerMoved = true;
                    this._state = TouchState.Moved;
                } else {
                    this._state = TouchState.Stationary;
                }
            }
        }

        if (this._pointerUp && !this._wasPointerUp) {
            this._wasPointerUp = true;
            this._state = TouchState.Ended;
        }

        this._inputManager.recordTouch(
            this._pointerPos,
            this._keyEventFromBools(
                this._state == TouchState.Began ||
                    this._state == TouchState.Moved ||
                    this._state == TouchState.Stationary,
                this._state == TouchState.Began,
                this._state == TouchState.Ended /* || this._state == TouchState.Cancelled */
            )
        );
    }

    onActivate(): void {
        const canvas = this.engine.canvas;

        canvas.addEventListener('pointerdown', this._onPointerDown, {passive: false});
        canvas.addEventListener('pointermove', this._onPointerMove, {passive: false});
        canvas.addEventListener('pointerup', this._onPointerUp);

        this._pointerDown = false;
        this._pointerPressed = false;
        this._pointerUp = false;
    }

    onDeactivate(): void {
        const canvas = this.engine.canvas;

        canvas.removeEventListener('pointerdown', this._onPointerDown);
        canvas.removeEventListener('pointermove', this._onPointerMove);
        canvas.removeEventListener('pointerup', this._onPointerUp);

        this._pointerDown = false;
        this._pointerPressed = false;
        this._pointerUp = false;
    }

    /* Pointer event handlers */

    private _onPointerDown = (e: PointerEvent) => {
        if (!e.isPrimary || e.button !== 0) {
            return;
        }

        this.updatePointPos(e);
        this._pointerDown = true;
        this._wasPointerDown = false;
        this._pointerUp = false;
    };

    private _onPointerMove = (e: PointerEvent) => {
        if (!e.isPrimary) {
            return;
        }

        // only track movement when pointer is down

        if (this._pointerDown) {
            this.updatePointPos(e);
            if (e.buttons === 0) {
                // pointer is up
                this._pointerDown = false;
                this._pointerUp = true;
            }
            this._pointerMoved = true;
            this._wasPointerMoved = false;
        }
    };

    private _onPointerUp = (e: PointerEvent) => {
        // Only handle primary button
        if (!e.isPrimary || e.button !== 0) {
            return;
        }

        this.updatePointPos(e);
        this._pointerUp = true;
        this._wasPointerUp = false;
        this._pointerDown = false;
    };

    private _keyEventFromBools(pressed: boolean, down: boolean, up: boolean): KeyEventType {
        let type = KeyEventType.None;
        if (up) {
            type = KeyEventType.Released;
        } else if (pressed) {
            if (down) {
                type = KeyEventType.Triggered;
            } else {
                type = KeyEventType.Pressed;
            }
        }

        return type;
    }

    private updatePointPos(e: PointerEvent) {
        this._pointerPos = [e.clientX, e.clientY];
    }
}
